import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { cccdAdmin, type AdminEvent, type AdminRegionalEvent, type BulkCreateTeamsResult } from '@/lib/api/cccdAdminClient';
import { parseTeamsCsv, type ParsedTeamRow } from '@/lib/teamsCsvImport';
import { pollImportJob } from '@/lib/importJobs';
import { getErrorMessage } from '@/lib/errors';
import { toastError } from '@/lib/toast';
import Alert from '@/components/ui/alert/Alert';
import { btnPrimary, btnSecondary } from '@/components/admin/FormComponents';

// Mismo patrón que StudentsImportPage/AcademicInstitutionsImportPage (subir →
// parsear client-side → previsualizar con errores/advertencias → confirmar),
// pero scoped a un solo evento/regional a la vez — ver
// openspec/changes/cccd-teams-institutions-csv-import.
export default function TeamsImportPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('eventId') ?? '';
  const regionalId = searchParams.get('regionalId') ?? '';
  const parentId = eventId || regionalId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parentEvent, setParentEvent] = useState<AdminEvent | AdminRegionalEvent | null>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedTeamRow[]>([]);
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ processed: number; total: number } | null>(null);
  const [result, setResult] = useState<BulkCreateTeamsResult | null>(null);
  const [rowFilter, setRowFilter] = useState<'all' | 'error' | 'warning'>('all');

  useEffect(() => {
    if (eventId) cccdAdmin.getEvent(eventId).then(setParentEvent).catch(() => setParentEvent(null));
    else if (regionalId) cccdAdmin.getRegional(regionalId).then(setParentEvent).catch(() => setParentEvent(null));
  }, [eventId, regionalId]);

  const validRows = rows.filter((r) => !r.error);
  const invalidRows = rows.filter((r) => r.error);
  const warningRows = validRows.filter((r) => r.warnings.length > 0);
  const warningCount = warningRows.length;
  const visibleRows = rowFilter === 'error' ? invalidRows : rowFilter === 'warning' ? warningRows : rows;

  function goBack() {
    navigate(eventId ? `/cccd/events/${eventId}` : `/cccd/regionals/${regionalId}`);
  }

  async function handleFile(file: File) {
    setFileName(file.name);
    setResult(null);
    setParseError('');
    setRows([]);
    setRowFilter('all');
    const text = await file.text();
    const { rows: parsed, error } = parseTeamsCsv(text, { hasCategoryPlan: Boolean(eventId) });
    if (error) {
      setParseError(error);
      return;
    }
    setRows(parsed);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  async function handleImport() {
    if (validRows.length === 0 || !parentId) return;
    setImporting(true);
    setProgress({ processed: 0, total: validRows.length });
    try {
      const { jobId } = await cccdAdmin.bulkCreateTeams({
        eventId: eventId || undefined,
        regionalEventId: regionalId || undefined,
        rows: validRows.map((r) => r.row),
      });
      const job = await pollImportJob<{ id: string; name: string }, { line: number; error: string }>(
        jobId,
        (j) => setProgress({ processed: j.processedRows, total: j.totalRows })
      ).promise;
      setResult({ created: job.created, skipped: job.skipped });
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: eventId ? ['event', eventId] : ['regional', regionalId] });
    } catch (err) {
      toastError(err);
      setParseError(getErrorMessage(err));
    } finally {
      setImporting(false);
      setProgress(null);
    }
  }

  function reset() {
    setFileName('');
    setRows([]);
    setParseError('');
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  if (!parentId) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <Alert variant="error" title="No se puede importar equipos" message="Abre 'Importar equipos' desde la pestaña Equipos de un evento o regional." />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button type="button" onClick={goBack} className="hover:text-brand-500">
          {parentEvent?.name ?? 'Evento'}
        </button>
        <span>/</span>
        <span className="text-gray-900 dark:text-white font-medium">Importar equipos</span>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Importar equipos desde CSV</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Sube un archivo .csv generado a partir de la plantilla de este evento. Un nombre de equipo que ya exista en
          este evento (comparado sin importar mayúsculas, tildes o espacios) se omite para no crear duplicados. Si la
          institución educativa de una fila no existe, se crea automáticamente.
        </p>

        {!result && (
          <div className="mt-4 flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200 dark:text-gray-400 dark:file:bg-gray-800 dark:file:text-gray-300"
            />
            {fileName && <span className="text-xs text-gray-500 dark:text-gray-400">{fileName}</span>}
          </div>
        )}

        {parseError && (
          <div className="mt-4">
            <Alert variant="error" title="No se pudo leer el archivo" message={parseError} />
          </div>
        )}

        {!result && rows.length > 0 && (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-sm">
              <button
                type="button"
                onClick={() => setRowFilter('all')}
                className={`rounded-lg px-3 py-1.5 font-medium ${rowFilter === 'all' ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
              >
                Todas ({rows.length})
              </button>
              {invalidRows.length > 0 && (
                <button
                  type="button"
                  onClick={() => setRowFilter('error')}
                  className={`rounded-lg px-3 py-1.5 font-medium ${rowFilter === 'error' ? 'bg-error-100 text-error-700 dark:bg-error-500/20 dark:text-error-400' : 'text-error-600 hover:bg-error-50 dark:hover:bg-error-500/10'}`}
                >
                  Con error ({invalidRows.length})
                </button>
              )}
              {warningCount > 0 && (
                <button
                  type="button"
                  onClick={() => setRowFilter('warning')}
                  className={`rounded-lg px-3 py-1.5 font-medium ${rowFilter === 'warning' ? 'bg-warning-100 text-warning-700 dark:bg-warning-500/20 dark:text-warning-400' : 'text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-500/10'}`}
                >
                  Con advertencia ({warningCount})
                </button>
              )}
              <span className="ml-auto text-success-600">{validRows.length} listos para importar</span>
            </div>

            <div className="mt-3 max-h-[32rem] overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-800">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2">Línea</th>
                    <th className="px-3 py-2">Equipo</th>
                    <th className="px-3 py-2">Categoría</th>
                    <th className="px-3 py-2">Plan</th>
                    <th className="px-3 py-2">Estado / detalle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-gray-400">
                        Ninguna fila en este filtro.
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((r) => (
                      <tr key={r.line} className={r.error ? 'bg-error-50/40 dark:bg-error-500/5' : r.warnings.length > 0 ? 'bg-warning-50/40 dark:bg-warning-500/5' : undefined}>
                        <td className="px-3 py-2 align-top text-gray-400">{r.line}</td>
                        <td className="px-3 py-2 align-top text-gray-800 dark:text-gray-200">{r.name || '—'}</td>
                        <td className="px-3 py-2 align-top text-gray-500">{r.row?.categoryId ?? '—'}</td>
                        <td className="px-3 py-2 align-top text-gray-500">{r.row?.planId ?? '—'}</td>
                        <td className="px-3 py-2 align-top">
                          {r.error ? (
                            <span className="text-xs font-medium text-error-600">{r.error}</span>
                          ) : r.warnings.length > 0 ? (
                            <ul className="list-inside list-disc space-y-0.5 text-xs font-medium text-warning-600">
                              {r.warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-xs font-medium text-success-600">Lista</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={reset} className={btnSecondary}>
                Cancelar
              </button>
              <button type="button" onClick={handleImport} disabled={importing || validRows.length === 0} className={btnPrimary}>
                {importing
                  ? `Importando... (${progress?.processed ?? 0}/${progress?.total ?? validRows.length})`
                  : `Importar ${validRows.length} equipo${validRows.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}

        {result && (
          <div className="mt-5 space-y-4">
            <Alert
              variant={result.skipped.length === 0 ? 'success' : 'warning'}
              title={`${result.created.length} equipo${result.created.length === 1 ? '' : 's'} creado${result.created.length === 1 ? '' : 's'}`}
              message={
                result.skipped.length === 0
                  ? 'Todas las filas se importaron correctamente.'
                  : `${result.skipped.length} fila${result.skipped.length === 1 ? '' : 's'} no se importó por duplicados o datos inválidos (detalle abajo).`
              }
            />

            {result.skipped.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-800">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2">Línea</th>
                      <th className="px-3 py-2">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {result.skipped.map((s) => (
                      <tr key={s.line}>
                        <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{s.line}</td>
                        <td className="px-3 py-2 text-gray-500">{s.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={reset} className={btnSecondary}>
                Importar otro archivo
              </button>
              <button type="button" onClick={goBack} className={btnPrimary}>
                Volver al evento
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
